import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import { Container, Reveal } from '@/components/ui'
import { SITE } from '@/content/site'
import { useSlotT } from '@/cms/useSlotT'

const STEP_ICONS = {
  before: 'lucide:search',
  during: 'lucide:wrench',
  after: 'lucide:check-circle',
}

export default function SupportProcess() {
  // Static header keys (tag/title) are CMS-overridable.
  // Step-level (dynamic site.process.*.title/description) stays on i18n for
  // now — Phase F block-based editor will support per-step CMS editing.
  //
  // 2026-11-04: the section moved off /products onto /about, so its copy is
  // edited under the 關於我們 scope now. The i18n keys moved with it
  // (`home.process.*` → `about.process.*`), because the section no longer
  // belongs to the homepage's copy tree either.
  const slotT = useSlotT('about')
  const { t } = useTranslation()

  return (
    <section className="py-section-y px-section-x bg-surface-2" aria-labelledby="process-title">
      <Container>
        <div className="text-center mb-6 md:mb-8">
          {/* The 「支援流程」 tagline is deleted at the owner's request — the
              title below (「我們如何與學校同行」) already names the section.
              It is removed from the markup rather than hidden with CSS, so
              it does not ship in the HTML; the copy survives in the CMS slot
              (`about.process.tag`) and can be restored by putting the span
              back. */}
          <h2
            id="process-title"
            className="text-3xl md:text-4xl font-bold text-ink"
          >
            {slotT('about.process.title')}
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-gap-cards relative">
          {/* Connector line (desktop) */}
          <div
            aria-hidden="true"
            className="hidden md:block absolute top-10 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-primary via-accent to-primary-dark"
          />

          {SITE.processSteps.map((step, idx) => (
            <Reveal key={step.step} delay={idx * 0.15}>
              <div className="text-center relative">
                {/* Phase 1 mobile (2026-10-13): step circle was 96px on
                    mobile too — three stacked 96px circles in a 1-col
                    mobile layout ate ~290px of vertical space. Cap at
                    64px mobile / 80px desktop; border 2 / 3; number
                    badge 28 / 32px. */}
                <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-surface border-2 md:border-[3px] border-primary shadow-yada-lg mb-4 md:mb-6 relative z-10">
                  <Icon
                    icon={STEP_ICONS[step.step as keyof typeof STEP_ICONS]}
                    className="text-primary"
                    style={{ fontSize: '1.75rem' }}
                  />
                  <span className="absolute -top-1.5 -right-1.5 md:-top-2 md:-right-2 bg-accent text-ink w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center font-bold text-xs md:text-sm shadow-yada">
                    {idx + 1}
                  </span>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-ink mb-2 md:mb-3">
                  {t(step.titleKey)}
                </h3>
                <p className="text-sm md:text-base text-ink-soft">{t(step.descriptionKey)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  )
}
